import React, { useState, useEffect, useMemo } from "react";
import { observer } from "mobx-react";
import { PauseEvent } from "@u-wave/react-vimeo";
import WorkoutStore from "stores/WorkoutStore";
import WorkoutDetailsStore from "stores/WorkoutDetailsStore";
import I36CalendarScheduled from "assets/icons/I36CalendarScheduled";
import I36Calendar from "assets/icons/I36Calendar";
import I36StarFilled from "assets/icons/I36StarFilled";
import I36Star from "assets/icons/I36Star";
import I36Check from "assets/icons/I36Check";
import I36CheckFilled from "assets/icons/I36CheckFilled";
import GridCollection from "components/GridCollection";
import Video from "components/Video";
import { SnackbarTypeValues } from "components/Snackbar";
import {
  SnackbarContextProps,
  withGlobalSnackbar,
} from "services/snackbar/snackbarContext";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import Spinner from "components/Spinner";
import AddInSchedulePopup from "components/AddInSchedulePopup";
import NoInternetConnectionPlaceholder from "components/NoInternetConnectionPlaceholder";
import CompleteWorkout from "./CompleteWorkout";
import { NoInternetConnectionError, NotFoundError } from "services/api";
import { redirectToRootRoute } from "utils/route";
import styleConst from "constants/style";
import {
  Button,
  Container,
  Controls,
  ControlWrapper,
  Equipments,
  EquipmentContainer,
  EquipmentImage,
  EquipmentImageContainer,
  EquipmentText,
  InfoContainer,
  Section,
  Text,
  TextWrapper,
  Title,
  TitleContainer,
  TitleAdornment,
  Workout,
  Loader,
  HorizontalSeparator,
  Image,
} from "./styles";
import { EVENT_TYPE, logEvent } from "utils/analytics";
import { zonedTimeToUTC, convertToUTCZero } from "utils/timezones";
import { useTranslation } from "react-i18next";
import { startOfDay } from "date-fns";

const SECONDS_TO_END = 30;

type Props = {
  workoutId: number;
  categoryId?: number;
  dayOfWeek?: number;
  scheduleDate?: string;
  workoutInWorkoutCategoryId?: number;
  onNavigateToMovement: (movement: any) => void;
  onChangeIsLoading?: (value: boolean) => void;
  onGoBack?: () => void;
  history?: any;
  familyId?: number;
} & SnackbarContextProps &
  GlobalPopupProps;

const WorkoutDetails: React.FC<Props> = observer(props => {
  const {
    workoutId,
    categoryId,
    dayOfWeek,
    scheduleDate,
    workoutInWorkoutCategoryId,
    onNavigateToMovement,
    onGoBack,
    history,
    familyId,
    setMessage,
    openPopup,
  } = props;

  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isScheduled, setIsScheduled] = useState<boolean>(!!scheduleDate);
  const [isNoInternetConnectionError, setIsNoInternetConnectionError] =
    useState<boolean>(false);

  const url = window.location.pathname;

  const details = WorkoutDetailsStore.details;

  // const workoutTitle = useMemo(() => {
  //   return details?.label || details?.title;
  // }, [details]);

  const equipments = useMemo(() => {
    return WorkoutDetailsStore.details?.equipments;
  }, []);

  const movements = useMemo(() => {
    return WorkoutDetailsStore.details?.movements;
  }, []);

  const videoFinishTime = useMemo(() => {
    return details?.duration
      ? Math.round(details.duration) - SECONDS_TO_END
      : null;
  }, [details]);

  const isDone = details?.isDone;

  const isDoneWithScheduleDate = scheduleDate && isDone;

  const awardStar = useMemo(() => {
    return WorkoutDetailsStore.details?.awardStar ?? true;
  }, []);

  const isDoneBtnVisible = useMemo(() => {
    if (!scheduleDate) return true;

    const convertedDate = startOfDay(new Date(scheduleDate));
    const now = startOfDay(new Date());

    const isVisible = now >= convertedDate;
    return isVisible;
  }, [scheduleDate]);

  useEffect(() => {
    const fetchData = async () => {
      const hasExistingData = WorkoutDetailsStore.details?.id === workoutId;
      if (!hasExistingData) {
        setIsLoading(true);
      }
      try {
        if (categoryId) {
          await WorkoutStore.fetchDetails(categoryId, false, familyId);
        }

        await WorkoutDetailsStore.fetch({
          categoryId,
          workoutId,
          scheduleDate,
          dayOfWeek,
          workoutInWorkoutCategoryId,
        });
      } catch (error) {
        if (error instanceof NoInternetConnectionError) {
          setIsNoInternetConnectionError(true);
        }

        if (error instanceof NotFoundError) {
          redirectToRootRoute(history, url);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();

    return () => {
      WorkoutDetailsStore.reset();
    };
  }, [
    categoryId,
    workoutId,
    scheduleDate,
    dayOfWeek,
    workoutInWorkoutCategoryId,
    familyId,
    history,
    url,
  ]);

  const favoriteWorkout = async () => {
    try {
      await WorkoutDetailsStore.addToFavorites();
    } catch (error: any) {
      setMessage(error?.message, SnackbarTypeValues.ALERT);
    }
  };

  const unFavoriteWorkout = async () => {
    try {
      await WorkoutDetailsStore.removeFromFavorites();
    } catch (error: any) {
      setMessage(error?.message, SnackbarTypeValues.ALERT);
    }
  };

  const scheduleWorkout = () => {
    openPopup({
      key: "AddInSchedulePopup",
      render: ({ close }: { close: () => void }) => (
        <AddInSchedulePopup
          onClose={() => {
            setIsScheduled(!!scheduleDate);
            close();
          }}
        />
      ),
      exitButtonColor: styleConst.colors.black50,
    });
  };

  const unScheduleWorkout = async () => {
    if (!scheduleDate) return true;

    if (scheduleDate) {
      setIsScheduled(false);
      await WorkoutDetailsStore.removeSchedule(convertToUTCZero(scheduleDate));
    }
    onGoBack?.();
  };

  const onCompleteWorkout = () => {
    openPopup({
      key: "CompleteWorkout",
      render: ({ close }: { close: () => void }) => (
        <CompleteWorkout
          details={details}
          onClose={close}
          isLoading={WorkoutDetailsStore.isUpdating}
          onSubmit={changeWorkout(close)}
          onAccept={completeWorkout}
          goBack={onGoBack}
        />
      ),
      exitButtonColor: styleConst.colors.black50,
    });
  };

  const undoComplete = async () => {
    try {
      await WorkoutDetailsStore.undoComplete(scheduleDate);
    } catch (error) {
      console.log("error :>> ", error);
    }
  };

  const changeWorkout = (close: () => void) => async (values: any) => {
    try {
      if (scheduleDate) {
        values.scheduleDate = scheduleDate;
      }
      await WorkoutDetailsStore.change(values);

      close();
      onGoBack?.();
    } catch (error: any) {
      setMessage(error?.message, SnackbarTypeValues.ALERT);
    }
  };

  const completeWorkout = async () => {
    const doneDateInUTC = scheduleDate
      ? convertToUTCZero(scheduleDate)
      : new Date();

    try {
      const payload = { doneDate: doneDateInUTC };

      await WorkoutDetailsStore.complete(payload);
    } catch (error: any) {
      setMessage(error?.message, SnackbarTypeValues.ALERT);
      throw new Error(error);
    }
  };

  const onVideoEnd = () => {
    onCompleteWorkout();
  };

  const onVideoPause = (event: PauseEvent) => {
    const currentVideoFinishTime =
      videoFinishTime ?? Math.round(event.duration) - SECONDS_TO_END;

    if (Math.round(event.seconds) >= currentVideoFinishTime) {
      onCompleteWorkout();
    }
  };

  const renderSchedule = () => {
    const isSchedule = isScheduled;

    const icon = isSchedule ? <I36CalendarScheduled /> : <I36Calendar />;
    const text = isSchedule ? t("workouts.unschedule") : t("workouts.schedule");
    const backgroundColor = isSchedule
      ? styleConst.colors.primary20
      : styleConst.colors.black10;
    const onClick = isSchedule ? unScheduleWorkout : scheduleWorkout;

    return (
      <Button
        text={text}
        height={60}
        iconLeft={icon}
        backgroundColor={backgroundColor}
        onClick={onClick}
        inverted
      />
    );
  };

  const renderDone = () => {
    const text = isDoneWithScheduleDate
      ? t("actions.undone")
      : t("actions.done");
    const DoneIcon = isDone ? <I36CheckFilled /> : <I36Check />;
    const backgroundColor = isDone
      ? styleConst.colors.primary20
      : styleConst.colors.black10;

    const onClick = isDoneWithScheduleDate ? undoComplete : onCompleteWorkout;

    return (
      <Button
        text={text}
        height={60}
        iconLeft={DoneIcon}
        onClick={onClick}
        backgroundColor={backgroundColor}
        inverted
      />
    );
  };

  const renderFavorite = () => {
    const isFavorite = details?.isFavorite;
    const icon = isFavorite ? <I36StarFilled /> : <I36Star />;
    const text = isFavorite ? t("workouts.unfavorite") : t("workouts.favorite");
    const backgroundColor = isFavorite
      ? styleConst.colors.primary20
      : styleConst.colors.black10;

    const onClick = isFavorite ? unFavoriteWorkout : favoriteWorkout;

    return (
      <Button
        text={text}
        height={60}
        iconLeft={icon}
        backgroundColor={backgroundColor}
        onClick={onClick}
        inverted
      />
    );
  };

  if (isNoInternetConnectionError) {
    return <NoInternetConnectionPlaceholder />;
  }

  if (isLoading) {
    console.log("isLoading :>> ", isLoading);
    return (
      <Loader>
        <Spinner large />
      </Loader>
    );
  }

  return (
    <Container>
      <Workout>
        {WorkoutDetailsStore.details?.videoUrl ? (
          <Video
            logEvent={() =>
              logEvent({
                event: EVENT_TYPE.PLAY_VIDEO,
                data: {
                  id: WorkoutDetailsStore.details?.id,
                  title: WorkoutDetailsStore.details?.label,
                  date: zonedTimeToUTC(new Date())?.toISOString(),
                },
              })
            }
            imageUrl={WorkoutDetailsStore.details.imageUrl ?? ""}
            video={WorkoutDetailsStore.details?.videoUrl}
            duration={WorkoutDetailsStore.details.duration ?? 0}
            onEnd={onVideoEnd}
            onPause={onVideoPause}
          />
        ) : (
          <Image imageUrl={WorkoutDetailsStore.details?.imageUrl} />
        )}
        {awardStar && (
          <Controls>
            <ControlWrapper>{renderSchedule()}</ControlWrapper>
            <ControlWrapper>{renderFavorite()}</ControlWrapper>
            {isDoneBtnVisible && (
              <ControlWrapper>{renderDone()}</ControlWrapper>
            )}
          </Controls>
        )}
      </Workout>
      <HorizontalSeparator />
      <InfoContainer>
        {awardStar && equipments?.length ? (
          <Section isEquipment>
            <TitleContainer>
              <TitleAdornment />
              <Title>{t("workouts.workout-details.equipment")}</Title>
            </TitleContainer>
            <Equipments>
              {equipments.map(equipment => (
                <EquipmentContainer key={equipment.id}>
                  <EquipmentImageContainer>
                    <EquipmentImage imageUrl={equipment.imageUrl} />
                  </EquipmentImageContainer>
                  <EquipmentText>{equipment.title}</EquipmentText>
                </EquipmentContainer>
              ))}
            </Equipments>
          </Section>
        ) : null}
        <Section>
          <TitleContainer>
            <TitleAdornment />
            <Title>{t("workouts.workout-details.instructions")}</Title>
          </TitleContainer>
          <TextWrapper>
            <Text>{details?.instruction}</Text>
          </TextWrapper>

          {movements?.length ? (
            <GridCollection
              items={WorkoutDetailsStore.details?.movements ?? []}
              onClick={onNavigateToMovement}
              isWorkout
            />
          ) : null}
        </Section>
      </InfoContainer>
    </Container>
  );
});

export default withGlobalSnackbar(withGlobalPopup(WorkoutDetails));
